// ** Redux Imports
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

// ** Axios Imports
import axios from 'axios'

// Fetch survey templates list
export const fetchTemplates = createAsyncThunk(
  'surveys/fetchTemplates',
  // `kind`: 'report' (the API default, what the assignment dropdown wants),
  // 'escalation', or 'all' — the templates list passes 'all' or the
  // escalation template disappears from it (FR-27).
  async ({ page = 1, limit = 20, search = '', includeArchived = false, kind }) => {
    const response = await axios.get('/v1/surveys/templates', {
      params: { page, limit, search, includeArchived, ...(kind ? { kind } : {}) }
    })
    return response.data
  }
)

// Fetch single template
export const fetchTemplate = createAsyncThunk(
  'surveys/fetchTemplate',
  async (templateId) => {
    const response = await axios.get(`/v1/surveys/templates/${templateId}`)
    return response.data
  }
)

// Fetch template versions
export const fetchTemplateVersions = createAsyncThunk(
  'surveys/fetchTemplateVersions',
  async (templateId) => {
    const response = await axios.get(`/v1/surveys/templates/${templateId}/versions`)
    return response.data
  }
)

// Create template
export const createTemplate = createAsyncThunk(
  'surveys/createTemplate',
  async (templateData) => {
    const response = await axios.post('/v1/surveys/templates', templateData)
    return response.data
  }
)

// Update template
export const updateTemplate = createAsyncThunk(
  'surveys/updateTemplate',
  async ({ templateId, data }) => {
    const response = await axios.put(`/v1/surveys/templates/${templateId}`, data)
    return response.data
  }
)

// Archive template
export const archiveTemplate = createAsyncThunk(
  'surveys/archiveTemplate',
  async (templateId) => {
    await axios.delete(`/v1/surveys/templates/${templateId}`)
    return templateId
  }
)

// Restore template
export const restoreTemplate = createAsyncThunk(
  'surveys/restoreTemplate',
  async (templateId) => {
    const response = await axios.post(`/v1/surveys/templates/${templateId}/restore`)
    return response.data
  }
)

// Duplicate template
export const duplicateTemplate = createAsyncThunk(
  'surveys/duplicateTemplate',
  async ({ templateId, name }) => {
    const response = await axios.post(`/v1/surveys/templates/${templateId}/duplicate`, { name })
    return response.data
  }
)

// Fetch data sources for auto-populated fields
export const fetchDataSources = createAsyncThunk(
  'surveys/fetchDataSources',
  async () => {
    const response = await axios.get('/v1/surveys/data-sources')
    return response.data
  }
)

// ============================================
// Survey Assignments
// ============================================

// Fetch survey assignments list
export const fetchAssignments = createAsyncThunk(
  'surveys/fetchAssignments',
  async ({ page = 1, limit = 20, customerId, templateId, isActive } = {}) => {
    const response = await axios.get('/v1/surveys/assignments', {
      params: { page, limit, customerId, templateId, isActive }
    })
    return response.data
  }
)

// Fetch single assignment
export const fetchAssignment = createAsyncThunk(
  'surveys/fetchAssignment',
  async (assignmentId) => {
    const response = await axios.get(`/v1/surveys/assignments/${assignmentId}`)
    return response.data
  }
)

// Create assignment
export const createAssignment = createAsyncThunk(
  'surveys/createAssignment',
  async (assignmentData) => {
    const response = await axios.post('/v1/surveys/assignments', assignmentData)
    return response.data
  }
)

// Update assignment
export const updateAssignment = createAsyncThunk(
  'surveys/updateAssignment',
  async ({ assignmentId, data }) => {
    const response = await axios.put(`/v1/surveys/assignments/${assignmentId}`, data)
    return response.data
  }
)

// Pause assignment
export const pauseAssignment = createAsyncThunk(
  'surveys/pauseAssignment',
  async (assignmentId) => {
    const response = await axios.post(`/v1/surveys/assignments/${assignmentId}/pause`)
    return response.data
  }
)

// Resume assignment
export const resumeAssignment = createAsyncThunk(
  'surveys/resumeAssignment',
  async (assignmentId) => {
    const response = await axios.post(`/v1/surveys/assignments/${assignmentId}/resume`)
    return response.data
  }
)

// Delete assignment
export const deleteAssignment = createAsyncThunk(
  'surveys/deleteAssignment',
  async (assignmentId) => {
    await axios.delete(`/v1/surveys/assignments/${assignmentId}`)
    return assignmentId
  }
)

// ============================================
// My Reports (Survey Instances)
// ============================================

// Fetch my report instances
export const fetchMyReports = createAsyncThunk(
  'surveys/fetchMyReports',
  async () => {
    const response = await axios.get('/v1/surveys/my-reports')
    return response.data
  }
)

// Update instance status
export const updateInstanceStatus = createAsyncThunk(
  'surveys/updateInstanceStatus',
  async ({ instanceId, status }) => {
    const response = await axios.put(`/v1/surveys/instances/${instanceId}/status`, { status })
    return response.data
  }
)

export const surveysSlice = createSlice({
  name: 'surveys',
  initialState: {
    // Templates state
    templates: [],
    pagination: {
      page: 1,
      limit: 20,
      totalItems: 0,
      totalPages: 0
    },
    currentTemplate: null,
    currentVersions: [],
    dataSources: null,
    // Assignments state
    assignments: [],
    assignmentsPagination: {
      page: 1,
      limit: 20,
      totalItems: 0,
      totalPages: 0
    },
    currentAssignment: null,
    // My Reports (Instances) state
    myReports: [],
    myReportsLoading: false,
    // Common state
    isLoading: false,
    isSaving: false,
    error: null
  },
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    clearCurrentTemplate: (state) => {
      state.currentTemplate = null
      state.currentVersions = []
    },
    clearCurrentAssignment: (state) => {
      state.currentAssignment = null
    },
    initializeNewTemplate: (state) => {
      state.currentTemplate = {
        id: null,
        name: '',
        description: '',
        currentVersion: {
          versionNumber: 1,
          fieldDefinitions: {
            fields: []
          }
        }
      }
      state.currentVersions = []
    },
    setCurrentTemplate: (state, action) => {
      state.currentTemplate = action.payload
    },
    updateCurrentTemplateField: (state, action) => {
      if (state.currentTemplate) {
        state.currentTemplate = {
          ...state.currentTemplate,
          ...action.payload
        }
      }
    },
    // Local field updates for builder
    addField: (state, action) => {
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        state.currentTemplate.currentVersion.fieldDefinitions.fields.push(action.payload)
      }
    },
    removeField: (state, action) => {
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        state.currentTemplate.currentVersion.fieldDefinitions.fields =
          state.currentTemplate.currentVersion.fieldDefinitions.fields.filter(
            (field) => field.id !== action.payload
          )
      }
    },
    updateField: (state, action) => {
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        const index = state.currentTemplate.currentVersion.fieldDefinitions.fields.findIndex(
          (field) => field.id === action.payload.id
        )
        if (index !== -1) {
          state.currentTemplate.currentVersion.fieldDefinitions.fields[index] = action.payload
        }
      }
    },
    reorderFields: (state, action) => {
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        state.currentTemplate.currentVersion.fieldDefinitions.fields = action.payload
      }
    },
    // Section children operations
    updateSectionChildren: (state, action) => {
      const { sectionId, children } = action.payload
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        const fields = state.currentTemplate.currentVersion.fieldDefinitions.fields
        const sectionIndex = fields.findIndex((f) => f.id === sectionId)
        if (sectionIndex !== -1) {
          fields[sectionIndex] = {
            ...fields[sectionIndex],
            children
          }
        }
      }
    },
    addSectionChild: (state, action) => {
      const { sectionId, child } = action.payload
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        const fields = state.currentTemplate.currentVersion.fieldDefinitions.fields
        const sectionIndex = fields.findIndex((f) => f.id === sectionId)
        if (sectionIndex !== -1) {
          if (!fields[sectionIndex].children) {
            fields[sectionIndex].children = []
          }
          fields[sectionIndex].children.push(child)
        }
      }
    },
    removeSectionChild: (state, action) => {
      const { sectionId, childId } = action.payload
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        const fields = state.currentTemplate.currentVersion.fieldDefinitions.fields
        const sectionIndex = fields.findIndex((f) => f.id === sectionId)
        if (sectionIndex !== -1 && fields[sectionIndex].children) {
          fields[sectionIndex].children = fields[sectionIndex].children.filter(
            (child) => child.id !== childId
          )
        }
      }
    },
    updateSectionChild: (state, action) => {
      const { sectionId, child } = action.payload
      if (state.currentTemplate?.currentVersion?.fieldDefinitions) {
        const fields = state.currentTemplate.currentVersion.fieldDefinitions.fields
        const sectionIndex = fields.findIndex((f) => f.id === sectionId)
        if (sectionIndex !== -1 && fields[sectionIndex].children) {
          const childIndex = fields[sectionIndex].children.findIndex((c) => c.id === child.id)
          if (childIndex !== -1) {
            fields[sectionIndex].children[childIndex] = child
          }
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch templates
      .addCase(fetchTemplates.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTemplates.fulfilled, (state, action) => {
        state.templates = action.payload.templates
        state.pagination = action.payload.pagination
        state.isLoading = false
      })
      .addCase(fetchTemplates.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Fetch single template
      .addCase(fetchTemplate.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTemplate.fulfilled, (state, action) => {
        state.currentTemplate = action.payload
        state.isLoading = false
      })
      .addCase(fetchTemplate.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Fetch versions
      .addCase(fetchTemplateVersions.fulfilled, (state, action) => {
        state.currentVersions = action.payload
      })
      // Create template
      .addCase(createTemplate.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(createTemplate.fulfilled, (state, action) => {
        state.templates.unshift(action.payload)
        state.currentTemplate = action.payload
        state.isSaving = false
      })
      .addCase(createTemplate.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message
      })
      // Update template
      .addCase(updateTemplate.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(updateTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex((t) => t.id === action.payload.id)
        if (index !== -1) {
          state.templates[index] = action.payload
        }
        state.currentTemplate = action.payload
        state.isSaving = false
      })
      .addCase(updateTemplate.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message
      })
      // Archive template
      .addCase(archiveTemplate.fulfilled, (state, action) => {
        state.templates = state.templates.filter((t) => t.id !== action.payload)
      })
      // Restore template
      .addCase(restoreTemplate.fulfilled, (state, action) => {
        const index = state.templates.findIndex((t) => t.id === action.payload.id)
        if (index !== -1) {
          state.templates[index] = action.payload
        }
      })
      // Duplicate template
      .addCase(duplicateTemplate.fulfilled, (state, action) => {
        state.templates.unshift(action.payload)
      })
      // Fetch data sources
      .addCase(fetchDataSources.fulfilled, (state, action) => {
        state.dataSources = action.payload
      })
      // ============================================
      // Assignment reducers
      // ============================================
      // Fetch assignments
      .addCase(fetchAssignments.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignments = action.payload.assignments
        state.assignmentsPagination = action.payload.pagination
        state.isLoading = false
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Fetch single assignment
      .addCase(fetchAssignment.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchAssignment.fulfilled, (state, action) => {
        state.currentAssignment = action.payload
        state.isLoading = false
      })
      .addCase(fetchAssignment.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message
      })
      // Create assignment
      .addCase(createAssignment.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(createAssignment.fulfilled, (state, action) => {
        state.assignments.unshift(action.payload)
        state.isSaving = false
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message
      })
      // Update assignment
      .addCase(updateAssignment.pending, (state) => {
        state.isSaving = true
        state.error = null
      })
      .addCase(updateAssignment.fulfilled, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.payload.id)
        if (index !== -1) {
          state.assignments[index] = action.payload
        }
        state.isSaving = false
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.isSaving = false
        state.error = action.error.message
      })
      // Pause assignment — optimistic: flip immediately, revert on failure
      .addCase(pauseAssignment.pending, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.meta.arg)
        if (index !== -1) state.assignments[index].isActive = false
      })
      .addCase(pauseAssignment.fulfilled, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.payload.id)
        if (index !== -1) {
          // Merge so template/customer associations are never dropped
          state.assignments[index] = { ...state.assignments[index], ...action.payload }
        }
      })
      .addCase(pauseAssignment.rejected, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.meta.arg)
        if (index !== -1) state.assignments[index].isActive = true
      })
      // Resume assignment — optimistic
      .addCase(resumeAssignment.pending, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.meta.arg)
        if (index !== -1) state.assignments[index].isActive = true
      })
      .addCase(resumeAssignment.fulfilled, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.payload.id)
        if (index !== -1) {
          state.assignments[index] = { ...state.assignments[index], ...action.payload }
        }
      })
      .addCase(resumeAssignment.rejected, (state, action) => {
        const index = state.assignments.findIndex((a) => a.id === action.meta.arg)
        if (index !== -1) state.assignments[index].isActive = false
      })
      // Delete assignment
      .addCase(deleteAssignment.fulfilled, (state, action) => {
        state.assignments = state.assignments.filter((a) => a.id !== action.payload)
      })
      // ============================================
      // My Reports (Instances) reducers
      // ============================================
      // Fetch my reports
      .addCase(fetchMyReports.pending, (state) => {
        state.myReportsLoading = true
        state.error = null
      })
      .addCase(fetchMyReports.fulfilled, (state, action) => {
        state.myReports = action.payload
        state.myReportsLoading = false
      })
      .addCase(fetchMyReports.rejected, (state, action) => {
        state.myReportsLoading = false
        state.error = action.error.message
      })
      // Update instance status
      .addCase(updateInstanceStatus.fulfilled, (state, action) => {
        const index = state.myReports.findIndex((r) => r.id === action.payload.id)
        if (index !== -1) {
          state.myReports[index] = action.payload
        }
      })
  }
})

export const {
  clearError,
  clearCurrentTemplate,
  clearCurrentAssignment,
  initializeNewTemplate,
  setCurrentTemplate,
  updateCurrentTemplateField,
  addField,
  removeField,
  updateField,
  reorderFields,
  updateSectionChildren,
  addSectionChild,
  removeSectionChild,
  updateSectionChild
} = surveysSlice.actions

export default surveysSlice.reducer
